// Phase 255-DemandTracker + Phase 255-Broadcast — Super Admin tile showing
// aggregate demand for the upcoming SocialX + StudioX add-ons, with an
// inline "Email Waitlist Backlog" broadcast trigger that re-engages every
// waitlist subscriber for the selected addon via Resend (background task).
//
// Mounted on /app/frontend/src/pages/SuperAdmin.js below the StreamX Studio
// launcher card. Pulls aggregate from `GET /api/super-admin/addons/demand-summary`.

import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Wand2, Film, BarChart3, Trophy, TrendingUp, Mail, Send, Loader2, History, CheckCircle2, XCircle, Calendar, RefreshCw } from 'lucide-react';

// Pre-filled subject + body so the operator can launch in one click. Kept
// short, on-brand, and easy to edit in the dialog.
const TEMPLATES = {
  socialx: {
    subject: 'SocialX private beta opens next week — claim your seat',
    body_text:
      'Quick update from the Out-Play team — we just greenlit SocialX (our AI omnichannel social-fan-out engine) based on real waitlist signal.\n\nWe\u2019re opening a private beta cohort next week to a handful of academies who want to test one-click AI captions, hero imagery, and reel publishing before the public launch.\n\nReply to this email if your academy is in.',
  },
  studiox: {
    subject: 'StudioX private beta — early seats now open',
    body_text:
      'Quick update from the Out-Play team — we just greenlit StudioX (browser-native upscaling + multi-track editor) based on real waitlist signal.\n\nNext week we open a small private beta cohort. Bring your raw training footage and we\u2019ll upscale it to 4K/8K and edit broadcast-grade reels straight from your dashboard.\n\nReply to this email if your academy wants in.',
  },
};

export default function AddonsDemandTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Broadcast composer state
  const [activeAddon, setActiveAddon] = useState(null); // 'socialx' | 'studiox' | null
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [sending, setSending] = useState(false);
  // Phase 255-History — campaign history drawer (read-only paper trail)
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Phase 255-DeltaResend — track per-row resend in-flight so we don't double-fire
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/super-admin/addons/demand-summary')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const openBroadcast = async (addon) => {
    const tpl = TEMPLATES[addon] || { subject: '', body_text: '' };
    setActiveAddon(addon);
    setSubject(tpl.subject);
    setBody(tpl.body_text);
    setPreviewCount(null);
    // Dry-run to preview recipient count before the operator confirms
    try {
      const r = await api.post('/super-admin/addons/waitlist-broadcast', {
        addon, subject: 'preview', body_text: 'preview', dry_run: true,
      });
      setPreviewCount(r.data?.recipients_count ?? 0);
    } catch {
      setPreviewCount(0);
    }
  };

  const closeBroadcast = () => {
    if (sending) return;
    setActiveAddon(null);
    setSubject('');
    setBody('');
    setPreviewCount(null);
  };

  const sendBroadcast = async () => {
    if (!activeAddon || !subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const r = await api.post('/super-admin/addons/waitlist-broadcast', {
        addon: activeAddon,
        subject: subject.trim(),
        body_text: body.trim(),
      });
      if (r.data?.status === 'queued') {
        toast.success(`Broadcast queued for ${r.data.recipients_count} ${activeAddon === 'socialx' ? 'SocialX' : 'StudioX'} waitlist subscribers`);
        closeBroadcast();
      } else if (r.data?.status === 'skipped') {
        toast.info(r.data.message || 'No active subscribers for this segment');
      } else {
        toast.info('Broadcast accepted');
        closeBroadcast();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Broadcast failed — check Resend configuration');
    } finally {
      setSending(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get('/super-admin/addons/broadcasts?limit=10');
      setHistory(r.data?.broadcasts || []);
    } catch {
      toast.error('Could not load broadcast history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) fetchHistory();
  };

  const formatTimestamp = (iso) => {
    if (!iso) return 'Recent';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <Card data-testid="addons-demand-tile-loading">
        <CardContent className="p-5">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card data-testid="addons-demand-tile-error" className="border-dashed">
        <CardContent className="p-5 text-center text-xs text-muted-foreground">
          Could not load AI add-ons demand tracker.
        </CardContent>
      </Card>
    );
  }

  const total = data.total_signups || 0;
  const socialxInterest = data.aggregate?.socialx_interest || 0;
  const studioxInterest = data.aggregate?.studiox_interest || 0;
  const exclusive = data.exclusive || { socialx: 0, studiox: 0, both: 0 };
  const verdict = data.verdict || {};
  const socialxPct = total > 0 ? Math.round((socialxInterest / total) * 100) : 0;
  const studioxPct = total > 0 ? Math.round((studioxInterest / total) * 100) : 0;
  const winner = verdict.winner;

  return (
    <>
      <Card data-testid="addons-demand-tile" className="border-emerald-200 dark:border-emerald-900/50">
        <CardContent className="p-5 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5 min-w-0">
              <div className="flex items-center gap-1.5">
                <BarChart3 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <h3 className="text-sm font-display font-bold truncate">AI Add-ons Demand Tracker</h3>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Real waitlist signals · {data.recent_signups_30d || 0} new in last 30 days
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-2xl font-display font-bold tabular-nums text-foreground" data-testid="addons-total-signups">{total}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Leads</p>
            </div>
          </div>

          {/* SocialX bar + broadcast trigger */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-semibold">
                <Wand2 className="h-3.5 w-3.5 text-emerald-600" /> SocialX Demand
                {winner === 'socialx' && <Trophy className="h-3 w-3 text-amber-500" data-testid="socialx-winner-badge" />}
              </span>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold tabular-nums" data-testid="socialx-interest-count">
                  {socialxInterest}
                  {total > 0 && <span className="text-muted-foreground font-medium text-[10px] ml-1">({socialxPct}%)</span>}
                </span>
                {socialxInterest > 0 && (
                  <button
                    type="button"
                    onClick={() => openBroadcast('socialx')}
                    className="text-[10px] font-bold text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200 flex items-center gap-1 transition-colors"
                    data-testid="broadcast-socialx-btn"
                  >
                    <Mail className="h-3 w-3" /> Email
                  </button>
                )}
              </div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                style={{ width: `${socialxPct}%` }}
                data-testid="socialx-bar"
              />
            </div>
          </div>

          {/* StudioX bar + broadcast trigger */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 font-semibold">
                <Film className="h-3.5 w-3.5 text-teal-600" /> StudioX Demand
                {winner === 'studiox' && <Trophy className="h-3 w-3 text-amber-500" data-testid="studiox-winner-badge" />}
              </span>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold tabular-nums" data-testid="studiox-interest-count">
                  {studioxInterest}
                  {total > 0 && <span className="text-muted-foreground font-medium text-[10px] ml-1">({studioxPct}%)</span>}
                </span>
                {studioxInterest > 0 && (
                  <button
                    type="button"
                    onClick={() => openBroadcast('studiox')}
                    className="text-[10px] font-bold text-teal-700 hover:text-teal-800 dark:text-teal-300 dark:hover:text-teal-200 flex items-center gap-1 transition-colors"
                    data-testid="broadcast-studiox-btn"
                  >
                    <Mail className="h-3 w-3" /> Email
                  </button>
                )}
              </div>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-teal-600 rounded-full transition-all duration-500"
                style={{ width: `${studioxPct}%` }}
                data-testid="studiox-bar"
              />
            </div>
          </div>

          {/* Exclusive breakdown */}
          <div className="grid grid-cols-3 gap-2 pt-1" data-testid="addons-exclusive-grid">
            <div className="rounded-lg bg-muted/40 border border-border/60 p-2 text-center">
              <p className="text-base font-display font-bold tabular-nums" data-testid="addons-exclusive-socialx">{exclusive.socialx}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">SocialX Only</p>
            </div>
            <div className="rounded-lg bg-muted/40 border border-border/60 p-2 text-center">
              <p className="text-base font-display font-bold tabular-nums" data-testid="addons-exclusive-studiox">{exclusive.studiox}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">StudioX Only</p>
            </div>
            <div className="rounded-lg bg-muted/40 border border-border/60 p-2 text-center">
              <p className="text-base font-display font-bold tabular-nums" data-testid="addons-exclusive-both">{exclusive.both}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Want Both</p>
            </div>
          </div>

          {/* Verdict footer */}
          <div
            data-testid="addons-verdict-banner"
            className={`rounded-lg border p-2.5 flex items-start gap-2 text-[11px] ${
              winner === 'socialx' ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-100' :
              winner === 'studiox' ? 'bg-teal-50 border-teal-200 text-teal-900 dark:bg-teal-950/40 dark:border-teal-800 dark:text-teal-100' :
              'bg-muted/40 border-border text-foreground'
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span className="leading-snug">{verdict.message}</span>
          </div>

          {/* Phase 255-History — campaign history toggle + drawer */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-muted-foreground">
              {showHistory ? 'Most recent broadcasts' : 'Broadcast log available'}
            </p>
            <button
              type="button"
              onClick={toggleHistory}
              className="text-[11px] font-bold text-muted-foreground hover:text-emerald-700 dark:hover:text-emerald-300 flex items-center gap-1 transition-colors"
              data-testid="addons-history-toggle"
            >
              <History className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin text-emerald-600' : ''}`} />
              {showHistory ? 'Hide history' : 'View past broadcasts'}
            </button>
          </div>

          {showHistory && (
            <div data-testid="addons-history-drawer" className="border-t border-border pt-3 space-y-2 max-h-64 overflow-y-auto">
              {historyLoading && history.length === 0 ? (
                <div className="py-6 flex justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                </div>
              ) : history.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground" data-testid="addons-history-empty">
                  No broadcasts sent yet. Use the Email triggers above to start your first campaign.
                </div>
              ) : (
                history.map((log) => {
                  const addonLabel = log.addon === 'socialx' ? 'SocialX' : log.addon === 'studiox' ? 'StudioX' : (log.addon || 'unknown').toUpperCase();
                  return (
                    <div
                      key={log.id || log.queued_at}
                      className="rounded-lg border border-border/60 bg-muted/30 p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                      data-testid={`broadcast-history-row-${log.id || 'unknown'}`}
                    >
                      <div className="space-y-0.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            log.addon === 'socialx' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200' :
                            log.addon === 'studiox' ? 'bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-200' :
                            'bg-muted text-muted-foreground'
                          }`}>{addonLabel}</span>
                          <span className="text-xs font-semibold truncate" title={log.subject}>{log.subject}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatTimestamp(log.queued_at)}</span>
                          <span className="opacity-40">·</span>
                          <span>{log.recipients_count || 0} recipient{(log.recipients_count || 0) === 1 ? '' : 's'}</span>
                          {log.status && log.status !== 'complete' && (
                            <>
                              <span className="opacity-40">·</span>
                              <span className="font-bold uppercase tracking-wider">{log.status}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 text-[11px] font-mono font-bold tabular-nums">
                        <div className="rounded-md border border-border bg-background px-2 py-1 flex items-center gap-1.5" title="Delivered">
                          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                          <span>{log.sent_count || 0}</span>
                        </div>
                        {(log.fail_count || 0) > 0 && (
                          <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/30 px-2 py-1 flex items-center gap-1.5 text-red-700 dark:text-red-300" title="Failed">
                            <XCircle className="h-3 w-3 text-red-500" />
                            <span>{log.fail_count}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Broadcast composer Dialog */}
      <Dialog open={!!activeAddon} onOpenChange={(o) => { if (!o) closeBroadcast(); }}>
        <DialogContent className="max-w-lg" data-testid="addons-broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" />
              Email {activeAddon === 'socialx' ? 'SocialX' : 'StudioX'} Waitlist
            </DialogTitle>
            <DialogDescription>
              Re-engage every academy on the waitlist for this add-on. Includes the exclusive segment AND the &quot;both&quot; overlap audience. Delivered via Resend in the background.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2 flex items-center justify-between" data-testid="broadcast-recipient-preview">
              <span className="text-xs text-emerald-900 dark:text-emerald-100">Will email</span>
              <span className="text-sm font-display font-bold tabular-nums text-emerald-900 dark:text-emerald-100">
                {previewCount === null ? '\u2026' : `${previewCount} subscriber${previewCount === 1 ? '' : 's'}`}
              </span>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="broadcast-subject" className="text-xs font-semibold">Subject</Label>
              <Input
                id="broadcast-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                data-testid="broadcast-subject-input"
              />
              <p className="text-[10px] text-muted-foreground text-right">{subject.length}/200</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="broadcast-body" className="text-xs font-semibold">Message (plain text — newlines preserved)</Label>
              <Textarea
                id="broadcast-body"
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={5000}
                className="text-sm leading-relaxed"
                data-testid="broadcast-body-input"
              />
              <p className="text-[10px] text-muted-foreground text-right">{body.length}/5000</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={closeBroadcast} disabled={sending} data-testid="broadcast-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={sendBroadcast}
              disabled={sending || !subject.trim() || !body.trim() || previewCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="broadcast-send-btn"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Queuing&hellip;</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send to {previewCount ?? '…'} {previewCount === 1 ? 'subscriber' : 'subscribers'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
